import { OneLineText } from '@/components/OneLineText';
import { Sheet, SheetContent, SheetHeader } from '@/components/ui/sheet';
import { useMediaGetById } from '@brainvaulttech/dgr-react-sdk';
import { Expand, FileText, Minimize2, Send, X } from 'lucide-react';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import '@cyntler/react-doc-viewer/dist/index.css';
import { useEffect, useMemo, useState } from 'react';
import Loader from './Loader';
import { DocumentListType } from '@/types/types';

const EmptyLoaderComponent = () => <Loader isLoading={true}> </Loader>;

export const DocumentListDialog = ({
  onCloseClick,
  documents,
  activeIndex = 0,
  isLoading = false,
  onRequestSendAffidavit,
}: {
  onCloseClick: () => void;
  documents: DocumentListType[];
  activeIndex?: number;
  isLoading?: boolean;
  /** When provided and a row has `affidavitSendDocumentId`, that row shows send for one document at a time. */
  onRequestSendAffidavit?: (affidavitSendDocumentId: string) => void;
}) => {
  const [selectedDocument, setSelectedDocument] = useState<DocumentListType | undefined>(
    typeof activeIndex === 'number' ? documents?.[activeIndex] : undefined
  );
  useEffect(() => {
    if (typeof activeIndex === 'number') setSelectedDocument(documents?.[activeIndex]);
  }, [activeIndex, documents]);

  const {
    data: mediaData,
    isError,
    isLoading: isMediaGetByIdLoading,
  } = useMediaGetById(selectedDocument?.media?.mediaId ?? '', {
    query: {
      staleTime: 1000 * 60 * 5,
      enabled: !!selectedDocument?.media?.mediaId,
      queryKey: ['media', selectedDocument?.media?.mediaId],
    },
  });

  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const memorizeDocument = useMemo(() => {
    if (isError) {
      return (
        <div className="flex w-full h-full justify-center items-center">
          <span className="text-error">Error Loading the Document</span>
        </div>
      );
    }
    if (mediaData)
      return (
        <Loader isLoading={isMediaGetByIdLoading}>
          <DocViewer
            data-testid="doc-viewer"
            className="custom-doc-viewer"
            key={selectedDocument?.media?.mediaId}
            config={{
              header: {
                disableHeader: true,
                disableFileName: true,
                retainURLParams: false,
              },
              pdfVerticalScrollByDefault: true,
              loadingRenderer: {
                overrideComponent: EmptyLoaderComponent,
              },
            }}
            documents={[
              {
                uri: mediaData?.data.url ?? '',
              },
            ]}
            style={{
              height: '100%',
              width: '100%',
              overflow: 'auto',
            }}
            prefetchMethod="GET"
            pluginRenderers={DocViewerRenderers}
          />
        </Loader>
      );
  }, [selectedDocument, mediaData, isError, isMediaGetByIdLoading]);
  return (
    <Sheet modal={true} open={true}>
      <SheetContent
        className="w-[80vw] h-full scrollbar-hide focus:outline-none py-8 px-8"
        tabIndex={-1}
        side={'center'}
        hideCloseButton={true}
      >
        <Loader isLoading={isLoading}>
          <SheetHeader>
            <div className="font-semibold text-xl flex flex-row justify-between">
              <span>{isFullScreen ? selectedDocument?.name : 'Documents'}</span>
              <span className="border border-bordercolor rounded-md p-1">
                <X
                  strokeWidth={1}
                  className="h-[20px] w-[20px] text-placeholder cursor-pointer"
                  onClick={onCloseClick}
                />
              </span>
            </div>
          </SheetHeader>
          {documents?.length ? (
            <div className={`grid ${isFullScreen ? 'grid-cols-1' : 'grid-cols-2'} gap-6 mt-4 h-[calc(100%-50px)]`}>
              {!isFullScreen && (
                <div className="flex flex-col gap-2 w-full h-full overflow-auto">
                  <div className="h-full overflow-auto pr-2 scrollbar-hide">
                    {documents?.map((document) => (
                      <div
                        key={document.media?.mediaId}
                        className={`flex flex-row gap-3 cursor-pointer items-center rounded-lg pt-4 p-3 mb-2 ${
                          selectedDocument?.media?.mediaId === document.media?.mediaId
                            ? 'bg-primary-foreground border-[1px] border-bordercolor '
                            : 'bg-[#F5F5F5]'
                        }`}
                        onClick={() => {
                          setSelectedDocument(document);
                        }}
                      >
                        <div className="shrink-0">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <OneLineText
                          text={document.name}
                          className="font-medium text-base text-primary flex-1 min-w-0"
                        />
                        {document.affidavitSendDocumentId && onRequestSendAffidavit ? (
                          <button
                            type="button"
                            aria-label={`Send ${document.name}`}
                            className="shrink-0 border border-bordercolor rounded-md p-1.5 bg-white hover:bg-[#EDF5FF]"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRequestSendAffidavit(document.affidavitSendDocumentId!);
                            }}
                          >
                            <Send className="h-4 w-4 text-primary" />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-[1px] border-bordercolor rounded-lg flex flex-col h-full overflow-hidden">
                <div className="flex flex-row justify-between items-center rounded-t-lg py-3 px-4 bg-[#F5F5F5] text-black gap-2">
                  <span className="font-medium text-base">Document Preview</span>
                  <span className="flex flex-row items-center gap-1 shrink-0">
                    {isFullScreen && selectedDocument?.affidavitSendDocumentId && onRequestSendAffidavit ? (
                      <button
                        type="button"
                        aria-label={`Send ${selectedDocument.name}`}
                        className="border-[1px] border-bordercolor p-1 bg-white rounded hover:bg-[#EDF5FF]"
                        onClick={() => onRequestSendAffidavit(selectedDocument.affidavitSendDocumentId!)}
                      >
                        <Send className="h-4 w-4 text-primary" />
                      </button>
                    ) : null}
                    <span className="border-[1px] border-bordercolor p-1 bg-white rounded">
                      {isFullScreen ? (
                        <Minimize2
                          className="h-4 w-4 text-placeholder cursor-pointer"
                          onClick={() => setIsFullScreen(!isFullScreen)}
                        />
                      ) : (
                        <Expand
                          className="h-4 w-4 text-placeholder cursor-pointer"
                          onClick={() => setIsFullScreen(!isFullScreen)}
                        />
                      )}
                    </span>
                  </span>
                </div>
                <div className="flex-1 overflow-auto">{memorizeDocument}</div>
              </div>
            </div>
          ) : (
            <div className="text-normal h-full w-full justify-center items-center flex text-placeholder font-[500]">
              No Documents Found
            </div>
          )}
        </Loader>
      </SheetContent>
    </Sheet>
  );
};
